import { Component, computed, inject, model, OnInit, signal } from '@angular/core';
import { MapComponent } from '../../../../../../../libs/home-building/src/lib/components/map/map.component';
import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import { MapsService } from '../../../../../../../libs/home-building/src/lib/services/maps.service';
import { ConfirmHelperService, rxResourceFixed } from '@zayan-erp/shared';
import { ActivatedRoute } from '@angular/router';
import { map, of, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { FileUploadService, SvgIconsComponent } from '../../../../../../../libs/shared/src';
import {
  Coordinates,
  LOT_STATUS_COLORS,
  MapLotResponse,
  MappingLot,
  ProjectsService,
  scheduledReservedLots
} from '../../../../../../../libs/home-building/src';
@Component({
  selector: 'app-project-map-assignment',
  standalone: true,
  templateUrl: './project-map-assignment.component.html',
  styleUrl: './project-map-assignment.component.scss',
  imports: [MapComponent, ProgressBarModule, SvgIconsComponent, FormsModule, NgClass]
})
export class ProjectMapAssignmentComponent implements OnInit {
  _mappingService = inject(MapsService);
  _queryParams = inject(ActivatedRoute);
  _projectService = inject(ProjectsService);
  _fileUploadService = inject(FileUploadService);
  #confirmHelperService = inject(ConfirmHelperService);
  mappedSearch = signal<string>('');
  unmappedSearch = signal<string>('');
  zoomMarker = signal<Coordinates | null>(null);
  zoomCounter = signal<number>(0);
  isDragOver = signal<boolean>(false);
  mappingIdToDelete = signal<string>('');
  confirmAction = signal<'deleteMap' | 'deleteMapping' | null>(null);

  projectId = model<string>(this._queryParams.snapshot.params['id'] || '');
  scheduledReservedLotIds = computed(() => new Set(this.scheduledReservedLots().map(lot => lot.lotId)));
  scheduledReservedLotInfoMap = computed(
    () =>
      new Map(
        this.scheduledReservedLots().map(lot => [
          lot.lotId,
          {
            dealId: lot.dealId,
            scheduledAt: lot.scheduledAt,
            realtorFullName: lot.realtorFullName,
            scheduleType: lot.scheduleType
          }
        ])
      )
  );
  coordinates = computed(() => {
    const lots = (this.mapCoordinates.value() as MappingLot[]) || [];
    const scheduledReservedLotIds = this.scheduledReservedLotIds();
    const scheduledReservedLotInfoMap = this.scheduledReservedLotInfoMap();

    // Keep lot membership unchanged, only override status for scheduled reservations.
    return lots.map(lot =>
      scheduledReservedLotIds.has(lot.lotId)
        ? {
            ...lot,
            status: 'ScheduledReserved',
            dealId: scheduledReservedLotInfoMap.get(lot.lotId)?.dealId ?? lot.dealId ?? null,
            scheduledAt: scheduledReservedLotInfoMap.get(lot.lotId)?.scheduledAt ?? lot.scheduledAt ?? null,
            realtorFullName: scheduledReservedLotInfoMap.get(lot.lotId)?.realtorFullName ?? lot.realtorFullName ?? null,
            scheduleType: scheduledReservedLotInfoMap.get(lot.lotId)?.scheduleType ?? lot.scheduleType ?? null
          }
        : lot
    );
  });
  mapImageUrl = computed(() => {
    return this.projectMap.value()?.['ImageUrl'] || '';
  });
  uploadedImageInfo = signal<{ s3Key: string; url: string } | null>(null);

  mappedLotsCount = computed(() => this.coordinates()?.filter(c => c?.lotCoordinateMapping !== null)?.length || 0);
  unmappedLotsCount = computed(() => this.coordinates()?.filter(c => c?.lotCoordinateMapping === null)?.length || 0);

  mappedLots = computed(() => {
    const coordinates = this.coordinates();
    const searchTerm = this.mappedSearch()?.toLowerCase();
    const mappedLots = coordinates?.filter(c => c?.lotCoordinateMapping !== null) ?? [];
    const scheduledReservedLotIds = this.scheduledReservedLotIds();

    const filteredMappedLots =
      searchTerm.length > 0
        ? mappedLots.filter((lot: MappingLot) => lot?.lotNumber?.toLowerCase()?.includes(searchTerm))
        : mappedLots;
    const nonScheduledMappedLots = filteredMappedLots.filter(lot => !scheduledReservedLotIds.has(lot.lotId));
    const scheduledMappedLots = filteredMappedLots
      .filter(lot => scheduledReservedLotIds.has(lot.lotId))
      .map(lot => ({ ...lot, status: 'ScheduledReserved' }));

    return [...nonScheduledMappedLots, ...scheduledMappedLots];
  });

  openLotsCount = computed(
    () =>
      this.mappedLots()?.filter(
        lot => lot.status === 'Open' && !this.scheduledReservedLots().some(scheduled => scheduled.lotId === lot.lotId)
      )?.length ?? 0
  );
  reservedLotsCount = computed(
    () =>
      this.mappedLots()?.filter(
        lot =>
          lot.status === 'Reserved' && !this.scheduledReservedLots().some(scheduled => scheduled.lotId === lot.lotId)
      )?.length ?? 0
  );
  soldLotsCount = computed(
    () =>
      this.mappedLots()?.filter(
        lot => lot.status === 'Sold' && !this.scheduledReservedLots().some(scheduled => scheduled.lotId === lot.lotId)
      )?.length ?? 0
  );
  offerLotsCount = computed(
    () =>
      this.mappedLots()?.filter(
        lot => lot.status === 'Offer' && !this.scheduledReservedLots().some(scheduled => scheduled.lotId === lot.lotId)
      )?.length ?? 0
  );
  scheduledReservedLots = signal<scheduledReservedLots[]>([]);

  unmappedLots = computed(() => {
    const coordinates = this.coordinates();
    const searchTerm = this.unmappedSearch()?.toLowerCase();
    const unmappedLots = coordinates?.filter(c => c?.lotCoordinateMapping === null) ?? [];
    const scheduledReservedLotIds = this.scheduledReservedLotIds();
    const filteredUnmappedLots =
      searchTerm.length > 0
        ? unmappedLots.filter((lot: MappingLot) => lot?.lotNumber?.toLowerCase()?.includes(searchTerm))
        : unmappedLots;
    const nonScheduledUnmappedLots = filteredUnmappedLots.filter(lot => !scheduledReservedLotIds.has(lot.lotId));
    const scheduledUnmappedLots = filteredUnmappedLots
      .filter(lot => scheduledReservedLotIds.has(lot.lotId))
      .map(lot => ({ ...lot, status: 'ScheduledReserved' }));

    return [...nonScheduledUnmappedLots, ...scheduledUnmappedLots];
  });

  readonly lotStatusColors = LOT_STATUS_COLORS;

  ngOnInit(): void {
    this._mappingService.markSaved$.subscribe({
      next: () => {
        this.mapCoordinates.reload();
        this.getScheduledReservedLots();
      }
    });
    this.getScheduledReservedLots();
  }

  mapCoordinates = rxResourceFixed({
    params: () => this.projectId(),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params) {
        return of({
          value: [],
          status: 0,
          isSuccess: false,
          successMessage: '',
          correlationId: '',
          location: '',
          errors: [],
          validationErrors: []
        } as MapLotResponse);
      }
      if (params === 'new') {
        return of(null);
      }
      return this._mappingService.getMapCoordinates(params).pipe(
        map((res: any) => {
          return res;
        })
      );
    }
  });

  projectMap = rxResourceFixed({
    params: () => this.projectId(),
    defaultValue: null,
    stream: ({ params }) => {
      if (params === 'new') {
        return of(null);
      }
      return this._projectService.getProjectById(params).pipe(
        switchMap((res: any) => {
          if (res?.mapImageUrl) {
            this._projectService.imageEmpty.set(false);
            return of({ ImageUrl: res.mapImageUrl });
          }
          this._projectService.imageEmpty.set(true);
          return of(null);
        })
      );
    }
  });

  onImageDropped(file: File) {
    this._fileUploadService
      .generatePublicUploadUrl(file.name)
      .pipe(
        switchMap(fileObject =>
          this._fileUploadService
            .uploadFileToS3({
              fileName: file.name,
              presignedUrl: fileObject.uploadUrl,
              s3Key: fileObject.s3Key,
              sourceFile: file
            })
            .pipe(
              map(uploadResult => ({
                documentId: fileObject.documentId,
                s3Key: fileObject.s3Key,
                url: uploadResult.presignedUrl
              }))
            )
        )
      )
      .subscribe({
        next: uploadResult => {
          this.uploadedImageInfo.set(uploadResult);
          this._mappingService
            .uploadMapImage(this.projectId(), uploadResult.documentId, { s3Key: uploadResult.s3Key })
            .subscribe({
              next: res => {
                this.projectMap.reload();
              }
            });
        }
      });
  }

  clearMap() {
    this._mappingService.deleteMapCoordinates(this.projectId()).subscribe({
      next: res => {
        this.projectMap.reload();
        this.mapCoordinates.reload();
        this.uploadedImageInfo.set(null);
      }
    });
  }

  deleteMappingById(id: string) {
    this._mappingService.deleteMappingById(id).subscribe({
      next: res => {
        this.mapCoordinates.reload();
      }
    });
  }

  zoomToMarker(coordinate: Coordinates | null) {
    this.zoomCounter.update(counter => counter + 1);
    this.zoomMarker.set(coordinate);
  }

  uploadMapImage() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.onImageDropped(file);
      }
    };
    fileInput.click();
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      this.onImageDropped(file);
    }
  }

  showConfirmPopup() {
    this.confirmAction.set('deleteMap');
    this.#confirmHelperService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Map ?',
        message: 'Are you sure you want to delete this map and mapping? This action is permanent and cannot be undone.',
        acceptLabel: 'Confirm',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.clearMap();
        } else {
          this.confirmAction.set(null);
        }
      });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        this.onImageDropped(file);
      }
    }
  }

  confirmDeleteMapping(id: string) {
    this.confirmAction.set('deleteMapping');
    this.mappingIdToDelete.set(id);
    this.#confirmHelperService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Mapping ?',
        message: 'Are you sure you want to delete this mapping? This action is permanent and cannot be undone.',
        acceptLabel: 'Confirm',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.deleteMappingById(this.mappingIdToDelete());
        } else {
          this.mappingIdToDelete.set('');
        }
      });
  }

  onConfirmAction() {
    const action = this.confirmAction();

    switch (action) {
      case 'deleteMap':
        this.clearMap();
        break;
      case 'deleteMapping':
        const id = this.mappingIdToDelete();
        if (id) {
          this.deleteMappingById(id);
          this.mappingIdToDelete.set('');
        }
        break;
    }

    this.confirmAction.set(null);
  }

  onCancel() {
    // Clear any stored IDs and action when user cancels
    this.mappingIdToDelete.set('');
    this.confirmAction.set(null);
  }

  getScheduledReservedLots() {
    this._mappingService.scheduledReservedLots(this.projectId()).subscribe({
      next: res => {
        this.scheduledReservedLots.set(res);
      }
    });
  }
}
